import type { FC } from 'react';
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import cn from 'classnames';

import type { TMarkdownContainer, TAllowedHeading } from '../models';
import { mutate } from '../helpers/mutate-html';
import type {
    IHeadingMarkdown,
    IListMarkdown,
    ILinkMarkdown,
    IImageMarkdown,
} from '../models/markdown-types';

// TODO: апунть ReactMarkdown до версии 8, или перейти на более легковесный фреймворк
export const MarkdownContainer: FC<React.PropsWithChildren<TMarkdownContainer>> = ({
    Aligner,
    Block,
    Br,
    Hr,
    Img,
    Link,
    List,
    Strong,
    Text,
    config,
    content,
    htmlContent = '',
    linkTargetBlank = false,
    renderers,
    styles,
}) => {
    const htmlContentMutated = useMemo(() => {
        return mutate(htmlContent || '');
    }, [htmlContent]);

    const paragraph: FC<React.PropsWithChildren<unknown>> = ({ children }) => (
        <Text styler={{ marginTop: 0, marginBottom: 12 }} tag="p">
            {children}
        </Text>
    );

    const heading: FC<React.PropsWithChildren<IHeadingMarkdown>> = ({ children, level }) => {
        const tag = `h${level}` as TAllowedHeading;

        return (
            <Text
                styler={{ marginTop: config[tag].mt, marginBottom: config[tag].mb }}
                tag={tag}
                view={config[tag].view}
            >
                {children}
            </Text>
        );
    };

    const link: FC<React.PropsWithChildren<ILinkMarkdown>> = ({ children, href }) => (
        <Link
            className={cn(styles.linkColor, styles.external)}
            href={href}
            {...(linkTargetBlank ? { target: '_blank' } : {})}
        >
            {children}
        </Link>
    );

    const image: FC<React.PropsWithChildren<IImageMarkdown>> = ({ alt, src }) => (
        <Aligner
            styler={{
                marginTop: config.img.mt,
                marginBottom: config.img.mb,
            }}
        >
            <Img alt={alt} src={src} />
        </Aligner>
    );

    const strong: FC<React.PropsWithChildren<unknown>> = ({ children }) => (
        <Strong>{children}</Strong>
    );

    const thematicBreak: FC<React.PropsWithChildren<unknown>> = () => (
        <Hr
            styler={{
                marginTop: config.hr.mt,
                marginBottom: config.hr.mb,
            }}
        />
    );

    const listItem: FC<React.PropsWithChildren<unknown>> = props => paragraph(props);

    const list: FC<React.PropsWithChildren<IListMarkdown>> = ({ children, ordered }) => {
        const stylesForListInMarkdown = cn(styles.external, styles.listMarkdown);

        if (ordered) {
            return (
                <List
                    className={stylesForListInMarkdown}
                    marker={null}
                    olRedMarker
                    olType="1"
                    type="ol"
                >
                    {children}
                </List>
            );
        }

        return (
            <List className={stylesForListInMarkdown} indentType={false} marker="•" type="ul">
                {children}
            </List>
        );
    };

    const breakFunc = () => <Br />;

    const blockquote: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
        return (
            <Block
                className={cn(styles.quote, styles.external)}
                notPadded
                styler={{
                    paddingBottom: config.blockquote.pb,
                    paddingTop: config.blockquote.pt,
                    paddingRight: config.blockquote.pt,
                    paddingLeft: config.blockquote.pt,
                    marginBottom: config.blockquote.mb,
                    marginTop: config.blockquote.mt,
                }}
            >
                {children}
            </Block>
        );
    };

    return (
        <ReactMarkdown
            renderers={{
                paragraph,
                heading,
                link,
                image,
                strong,
                thematicBreak,
                list,
                listItem,
                break: breakFunc,
                blockquote,
                ...renderers,
            }}
        >
            {(htmlContentMutated || content) ?? ''}
        </ReactMarkdown>
    );
};
